import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chatbot, ChatbotTemplate } from '../types/chatbot';
import { AI_AGENTS_DATA } from '../data/agents';
import { agentInstanceService } from '../services/agentService';
import { chatbotService } from '../services/chatbotService';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { toast } from 'react-toastify';
import { convertMillisecondsToSeconds } from '../utility/time';

import { LogoUploader, S3Params } from '../components/LogoUploader';
import { ShowImage } from '../utils/ShowImage';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../components/ui/Field';
import { Overlay } from '../components/ui/Overlay';
import { Surface } from '../components/ui/Surface';


export const Chatbots: React.FC = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [greetingMessage, setGreetingMessage] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');

  const [agentInstances, setAgentInstances] = useState<any[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);


  const [dynamicChatbots, setDynamicChatbots] = useState<any>([]);
  const [chatbotsLoading, setChatbotsLoading] = useState(false);
  const [chatbotsError, setChatbotsError] = useState<string | null>(null);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editGreeting, setEditGreeting] = useState('');
  const [editPlaceholder, setEditPlaceholder] = useState('');
  const [editSelectedAgentIds, setEditSelectedAgentIds] = useState<string[]>([]);
  const [editingSaving, setEditingSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

    // --- logo states (cropped data URLs) ---
    const [createLogo, setCreateLogo] = useState<S3Params | null>(null);
    const [editLogo, setEditLogo] = useState<S3Params | null>(null);



  const displayedChatbots : any = dynamicChatbots && dynamicChatbots.length > 0 ? dynamicChatbots : [];


  // ---- load agent instances for picker (dynamic-only) ----
  useEffect(() => {
    let mounted = true;
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        const resp = await agentInstanceService.listAgentInstance({ });
        const items = (resp as any).agentInstances ?? resp ?? [];
        const mapped = (items || []).map((it: any) => ({
          id: it.id,
          name: it.name ?? it.code ?? `Agent ${it.id}`,
          lastActive: it.updatedAt ? new Date(it.updatedAt).toLocaleString() : undefined,
          type: it.agentType?.name ?? it.agentType?.code ?? 'unknown',
        }));
        if (mounted) setAgentInstances(mapped);
      } catch (err) {
        console.error('Failed to load agent instances', err);
        if (mounted) setAgentInstances([]); 
      } finally {
        if (mounted) setAgentsLoading(false);
      }
    };
    loadAgents();
    return () => { mounted = false; };
  }, []);


  const loadChatbots = async () => {
    setChatbotsLoading(true);
    setChatbotsError(null);
    try {
      const res = await chatbotService.listChatbots({search:searchTerm});
      const items = (res as any).chatbots ?? res ?? [];
      const mapped = (items || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        description: c.description,
        imgLink:c.imgLink,
        s3ObjectData:c.s3ObjectData,
        type: 'general',
        status: c.state === 'active' ? 'active' : (c.state ?? 'inactive'),
        model: c.model ?? 'N/A',
        version: `v${c.version ?? c.schemaVersion ?? 1}`,
        language: c.language ?? 'English',
        createdAt: c.createdAt ? new Date(c.createdAt) : new Date(),
        updatedAt: c.updatedAt ? new Date(c.updatedAt) : new Date(),
        conversations: c._count?.conversations?? 0,
        agentsCount: (c.agents ?? []).length,
        satisfaction: c.metrics?.satisfaction ?? 0,
        responseTime: c.metrics?.responseTime ?? '—',
        tags: Array.from(
          new Set(
            (c.agents ?? [])
              .map((a: any) => a.agentInstance?.category?.name)
              .filter((name: unknown): name is string => Boolean(name))
          )
        ),
        configuration: c.config ?? {},
        avgResponseTime: c?.avgResponseTime ?? 0,
        __raw: c
      }));
      setDynamicChatbots(mapped);
    } catch (err: any) {
      console.error('Failed to load chatbots', err);
       {
        setChatbotsError('Failed to load chatbots');
        setDynamicChatbots([]); 
      }
    } finally {
      setChatbotsLoading(false);
    }
  };
  useEffect(() => {
    loadChatbots();
  }, []);

    useEffect(() => {
      let t = setTimeout(() => {
        loadChatbots();
      }, 300);
      return () => clearTimeout(t);
    }, [searchTerm]);

  const pickerAgents = useMemo(() => agentInstances || [], [agentInstances]);

  // ---------- Edit flow helpers ----------
  const openEdit = (chatbot: any) => {
    const raw = (chatbot as any).__raw ?? chatbot;
    setEditingId(chatbot.id);
    setEditName(raw.name ?? chatbot.name ?? '');
    setEditDescription(raw.description ?? chatbot.description ?? '');
    setEditGreeting(raw.config?.greeting ?? raw.configuration?.welcomeMessage ?? '');
    setEditPlaceholder(raw.config?.placeholder ?? '');
    const agentIds = (raw.agents ?? []).map((a: any) => a.agentInstanceId).filter(Boolean);
    setEditSelectedAgentIds(agentIds);
    setIsEditOpen(true);
    setEditLogo(raw.s3ObjectData ?? " ");
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditGreeting('');
    setEditPlaceholder('');
    setEditSelectedAgentIds([]);
    setEditLogo(null);
  };

  const doUpdate = async () => {
    if (!editingId) return;
    setEditingSaving(true);


    try {
      const payload: any = {
        name: editName.trim(),
        description: editDescription.trim(),
        config: {
          greeting: editGreeting || undefined,
          placeholder: editPlaceholder || undefined,
            // logo: editLogo,
        },
        s3ObjectData:editLogo,
        agentInstanceIds: editSelectedAgentIds,
      };
      const updated = await chatbotService.updateChatbot(editingId, payload);
      toast.success(updated?.message||"Chatbot updated successfully!")
      loadChatbots()
      
      closeEdit();
    } catch (err) {
      console.error('Failed to update chatbot', err);
    } finally {
      setEditingSaving(false);
    }
  };

  // ---------- Delete flow ----------
  const askDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const res:any = await chatbotService.deleteChatbot(deleteId);
      toast.success(res?.message||"Chatbot deleted successfully!")
      loadChatbots()

    } catch (err) {
      console.error('Delete failed', err);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  // ---------- Create flow (unchanged, but posts to API) ----------
  const doCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!createName.trim() || !createDescription.trim() || selectedAgentIds.length === 0) return;
    const code = createName.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '').slice(0, 100);
    const payload: any = {
      name: createName.trim(),
      code,
      description: createDescription.trim(),
      isActive: true,
      state: 'draft',
      config: {
        greeting: greetingMessage || undefined,
        placeholder: placeholderText || undefined,
        // logo: createLogo,
      },
      s3ObjectData:createLogo,
      schemaVersion: 1,
      agentInstanceIds: selectedAgentIds,
    };
    try {
      const created = await chatbotService.createChatbot(payload);
      setIsCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      setGreetingMessage('');
      setPlaceholderText('');
      setSelectedAgentIds([]);
      setAgentPickerOpen(false);
      setAgentSearch('');
      setCreateLogo(null);
      toast.success(created?.message||"Chatbot created successfully!")
      loadChatbots()
      
    } catch (err) {
      console.error('Create chatbot failed', err);
    }
  };

  // ---------- filteredChatbots (unchanged) ----------
  const filteredChatbots = displayedChatbots.filter((chatbot:any) => {
    const matchesSearch = chatbot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chatbot.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (chatbot.tags ?? []).some((tag:any) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType =
      typeFilter === 'all' ||
      (chatbot.tags ?? []).some((tag:any) => String(tag).toLowerCase() === typeFilter.toLowerCase());

    return matchesSearch && matchesType;
  });

  const categoryOptions = useMemo(() => {
    const allTags = (displayedChatbots || []).flatMap((c: any) => c.tags || []);
    const unique = Array.from(new Set(allTags.map((t: any) => String(t).trim()).filter(Boolean))) as string[];
    return unique.sort((a, b) => a.localeCompare(b));
  }, [displayedChatbots]);

  const activeConversations = useMemo(
    () => filteredChatbots.reduce((acc: number, c: any) => acc + Number(c.conversations || 0), 0),
    [filteredChatbots]
  );

  // ---------- UI (kept identical except added Edit/Delete actions & modals) ----------
  return (
    <Page
      containerSize="content"
      backgroundClassName="min-h-0 bg-[linear-gradient(180deg,#FFFFFF_0%,#4CC4D012_100%)]"
      contentClassName="pb-10 pt-6"
    >
      <PageHeader
        className="border-b border-c-e3e7f3 pb-6 pt-0"
        title={
          <>
            Live from your <span className="bg-gradient-to-r from-c-805dc6 to-c-3683c6 bg-clip-text text-transparent">AI Pods</span>
          </>
        }
        subtitle="A real-time view of your active chatbots — from healthcare assistants to code companions. Watch them engage, respond, and evolve as they handle thousands of conversations autonomously."
        aside={
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative w-full sm:w-[380px]">
              <TextField
                type="text"
                placeholder="Search by name or purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-c-c3c3c3 bg-white px-4 py-3 pr-11 text-[1rem] text-c-1f1f1f placeholder:text-c-9095a9 focus:border-c-8f8f8f focus:outline-none"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-c-5f79ce">⌕</span>
            </div>

            <SelectField
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full rounded-xl border border-c-c3c3c3 bg-white px-4 py-3 text-[1rem] text-c-1f1f1f sm:w-[230px]"
            >
              <option value="all">All Categories</option>
              {categoryOptions.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </SelectField>
          </div>
        }
      />

      <Section className="mt-8 grid items-start gap-6 pt-0 lg:grid-cols-[380px_1fr]">
            <aside className="lg:pr-6 lg:border-r lg:border-c-e3e7f3">
              <div className="mb-6 flex items-end gap-3">
                <p
                  className="text-[4.8rem] font-semibold leading-none text-transparent"
                  style={{ WebkitTextStroke: '1.7px #8ba0e8' }}
                >
                  {activeConversations.toLocaleString()}
                </p>
                <p className="pb-2 text-[2rem] leading-8 text-c-6f7281">Active Conversations</p>
              </div>

              <div className="space-y-3 border-t border-c-e3e7f3 pt-6">
                <h2 className="text-[2rem] font-semibold leading-tight text-c-111111">See what your bots are doing right now</h2>
                <p className="text-[1rem] leading-relaxed text-c-5f6678">
                  Stay updated on your deployed chatbots that are continuously engaging users, managing workflows, and delivering insights.
                </p>

                <p className="pt-2 text-[1.5rem] font-semibold text-c-7f58ca">
                  {activeConversations.toLocaleString()} interactions <span className="text-[1.1rem] font-normal text-c-6f7281">in the last 24 hours</span>
                </p>
                <span className="inline-flex items-center rounded-[8px] bg-c-edf9ec px-3 py-1 text-sm font-semibold text-[#13b546]">+9% from yesterday ↗</span>

                <div className="pt-2 space-y-3">
                  {/* <button className="w-full rounded-[12px] border border-c-3683c6 px-4 py-3 text-[1.2rem] font-medium text-c-1a1a1a">
                    View All Conversations ↗
                  </button> */}
                  <Button tone="brand-gradient"
                    onClick={() => setIsCreateOpen(true)}
                    className="w-full rounded-[12px] px-4 py-3 text-[1.2rem] font-medium"
                  >
                    Create New Bot ↗
                  </Button>
                </div>
              </div>
            </aside>

            {filteredChatbots.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-[20px] border border-dashed border-c-c9d3ee bg-white/60 px-6 py-16 text-center">
                <div className="mb-4 text-6xl">🤖</div>
                <h3 className="mb-2 text-xl font-semibold text-agentic-text-primary">No chatbots found</h3>
                <p className="mb-6 text-agentic-text-secondary">Create your first AI chatbot to get started</p>
                <Button tone="custom"
                  onClick={() => setIsCreateOpen(true)}
                  className="rounded-xl bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light px-6 py-3 font-medium text-white transition-all duration-200 hover:shadow-lg"
                >
                  Create Your First Bot
                </Button>
              </div>
            ) : (
            <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
              {filteredChatbots.map((chatbot: any, index: number) => {
                const showExtended = index === 2 || index === 3;
                const responseSeconds = chatbot?.avgResponseTime ? `${convertMillisecondsToSeconds(chatbot.avgResponseTime)}s` : '—';
                return (
                  <Surface as="article"
                    key={chatbot.id}
                    className="h-fit self-start rounded-[20px] border border-c-9cb7ec bg-white p-6 shadow-[0_1px_0_rgb(var(--c-3a5498,58_84_152)/0.08)]"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {(chatbot?.s3ObjectData || chatbot?.imgLink) ? (
                          <ShowImage
                            s3Params={chatbot?.s3ObjectData}
                            src={chatbot?.imgLink}
                            alt={chatbot.name}
                            className="h-12 w-12 rounded-xl object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-c-805dc6 to-c-3683c6" />
                        )}
                        <div>
                          <h3 className="text-[1.55rem] font-semibold leading-tight text-c-1f1f1f">{chatbot.name}</h3>
                          <p className="text-sm text-c-808080">specialized • GPT-4</p>
                        </div>
                      </div>
                      <span className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[8px] bg-c-edf9ec px-2.5 text-sm font-medium text-[#13b546] whitespace-nowrap">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#13b546]" />
                        Active
                      </span>
                    </div>

                    <p className="mt-3 text-[1.05rem] leading-relaxed text-c-1f1f1f">
                      {chatbot.description || 'AI-powered appointment scheduling and management for healthcare providers'}
                    </p>

                    {chatbot.tags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {chatbot.tags.slice(0, 3).map((tag: any) => (
                          <span key={`${chatbot.id}-${tag}`} className="rounded-full bg-c-eeebf7 px-3 py-1.5 text-sm font-medium text-c-6f6fbe">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 border-t border-c-e3e7f3 pt-4">
                      <div className="flex items-end gap-3">
                        <div className="grid flex-1 grid-cols-2 gap-3 min-w-0">
                          <div className="min-w-0 rounded-xl bg-c-f5f7fa px-3 py-2">
                            <p className="text-[1.35rem] font-semibold text-c-7f58ca">{(chatbot.conversations ?? 0).toLocaleString()}</p>
                            <p className="break-words text-sm text-c-808080">Conversations</p>
                          </div>
                          <div className="min-w-0 rounded-xl bg-c-f5f7fa px-3 py-2">
                            <p className="text-[1.35rem] font-semibold text-c-7f58ca">{chatbot.agentsCount ?? 0}</p>
                            <p className="text-sm text-c-808080">Agents</p>
                          </div>
                        </div>
                        <Button tone="custom"
                          onClick={() => navigate(`/chatbots/${chatbot.id}/config`)}
                          className="inline-flex h-10 w-[150px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[24px] border border-c-3683c6 px-4 text-sm font-medium text-c-1f1f1f"
                        >
                          <span>Details</span>
                          <span className="text-[12px] leading-none text-c-3f4a78">⌄</span>
                        </Button>
                      </div>

                      {showExtended && (
                        <>
                          <div className="mt-4 border-t border-c-e3e7f3 pt-3 text-sm text-c-5d6377">
                            Response Time: {responseSeconds} <span className="px-2 text-c-b7b7b7">|</span> Language: English <span className="px-2 text-c-b7b7b7">|</span> Version: {chatbot.version}
                          </div>
                          <div className="mt-3 flex gap-2">
                            <Button tone="custom"
                              onClick={() => navigate(`/chatbots/${chatbot.id}/config`)}
                              className="flex-1 rounded-[12px] border border-c-3683c6 px-3 py-2 text-[1.05rem] font-medium text-c-1f1f1f"
                            >
                              Configure
                            </Button>
                            <Button tone="brand-gradient"
                              onClick={() => openEdit(chatbot)}
                              className="w-[88px] rounded-[12px] px-3 py-2 text-[1.05rem] font-medium"
                            >
                              Test
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Surface>
                );
              })}
            </div>
            )}
      </Section>

      {/* Create Chatbot Modal (unchanged behaviour but integrated with API) */}
      {isCreateOpen && (
        <Overlay className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsCreateOpen(false)} />
          <Surface tone="popover" className="relative w-full max-w-4xl mx-4 rounded-2xl border p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">Create Chatbot</h2>
              <p className="text-sm text-agentic-text-secondary mt-1">Define a new chatbot and assign agents.</p>
            </div>
            <form onSubmit={doCreate} className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Chatbot Name</label>
                  <TextField tone="default-focus-primary"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="e.g., Pacific One Health"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Description</label>
                  <TextArea tone="default-focus-primary"
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    placeholder="Describe this chatbot"
                    rows={4}
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div>
                  <LogoUploader
                    label="Chatbot Logo"
                    value={createLogo}
                    onChange={setCreateLogo}
                  />
                </div>


                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Assign Agents</label>
                  <div
                    className="w-full rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-agentic-primary-blue focus-within:border-transparent px-2 py-2 flex flex-wrap gap-2 relative cursor-text"
                    tabIndex={0}
                    onClick={() => setAgentPickerOpen(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAgentPickerOpen((v) => !v); } }}
                    aria-expanded={agentPickerOpen}
                    role="combobox"
                    aria-haspopup="listbox"
                  >
                    {selectedAgentIds.length === 0 && (
                      <span className="text-sm text-agentic-text-muted px-1 py-1">Select agents…</span>
                    )}
                    {selectedAgentIds.map((id) => {
                      const agent = pickerAgents.find((a) => a.id === id);
                      if (!agent) return null;
                      return (
                        <Badge tone="primary-soft" key={id} className="px-2 py-1 rounded-full text-xs inline-flex items-center gap-2">
                          {agent.name} • Last updated: {agent.lastActive}
                          <Button tone="custom"
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setSelectedAgentIds(selectedAgentIds.filter((x) => x !== id)); }}
                            className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark"
                            aria-label="Remove agent"
                          >
                            ×
                          </Button>
                        </Badge>
                      );
                    })}
                    <div className="flex-1 min-w-[200px]" />
                    <Button tone="muted-text"
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setAgentPickerOpen((v) => !v); }}
                      className="absolute right-2 top-2 px-2 py-1"
                      aria-label="Toggle agents"
                    >
                      ▾
                    </Button>

                    {agentPickerOpen && (
                      <Surface tone="card-raised" className="absolute left-0 top-full mt-2 w-full max-h-64 overflow-y-auto border rounded-xl z-10">
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                          <TextField tone="default-focus-primary"
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                            placeholder="Search agents"
                            className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                          />
                        </div>
                        <div className="p-2">
                          {agentsLoading && <div className="p-2 text-sm text-agentic-text-secondary">Loading agents...</div>}
                          {!agentsLoading && pickerAgents.length === 0 && <div className="p-2 text-sm text-agentic-text-secondary">No agents available</div>}
                          {!agentsLoading && pickerAgents.length > 0 && pickerAgents
                            .filter((a) => a.name.toLowerCase().includes(agentSearch.toLowerCase()))
                            .map((agent) => {
                              const checked = selectedAgentIds.includes(agent.id);
                              return (
                                <Button tone="custom"
                                  type="button"
                                  key={agent.id}
                                  onClick={() => {
                                    if (checked) setSelectedAgentIds(selectedAgentIds.filter((x) => x !== agent.id));
                                    else setSelectedAgentIds([...selectedAgentIds, agent.id]);
                                  }}
                                  className={`w-full text-left flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 ${checked ? 'bg-agentic-primary-blue/5' : ''}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Checkbox readOnly checked={checked} className="pointer-events-none" />
                                    <div>
                                      <div className="text-sm font-medium text-agentic-text-primary">{agent.name}</div>
                                      <div className="text-xs text-agentic-text-secondary">Last updated: {agent.lastActive}</div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-agentic-text-secondary">{agent.type}</span>
                                </Button>
                              );
                            })}
                        </div>
                        <div className="sticky bottom-0 bg-white p-2 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs text-agentic-text-secondary">{selectedAgentIds.length} selected</span>
                          <div className="flex items-center gap-2">
                            <Button tone="outline" type="button" className="px-3 py-1 text-sm rounded-lg border" onClick={() => setSelectedAgentIds([])}>Clear</Button>
                            <Button tone="primary" type="button" className="px-3 py-1 text-sm rounded-lg hover:opacity-90"    onClick={(e) => { e.stopPropagation(); setAgentPickerOpen((v) => !v); }}>Done</Button>
                          </div>
                        </div>
                      </Surface>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Greeting Message</label>
                  <TextField tone="default-focus-primary"
                    value={greetingMessage}
                    onChange={(e) => setGreetingMessage(e.target.value)}
                    placeholder="Hello! How can I help you today?"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Placeholder Text</label>
                  <TextField tone="default-focus-primary"
                    value={placeholderText}
                    onChange={(e) => setPlaceholderText(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button tone="outline-muted"
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </Button>
                <Button tone="primary-gradient"
                  type="submit"
                  className="px-4 py-2 rounded-lg disabled:opacity-60"
                  disabled={!createName.trim() || !createDescription.trim() || selectedAgentIds.length === 0}
                >
                  Create
                </Button>
              </div>
            </form>
          </Surface>
        </Overlay>
      )}

      {/* Edit Chatbot Modal */}
      {isEditOpen && (
        <Overlay className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => closeEdit()} />
          <Surface tone="popover" className="relative w-full max-w-4xl mx-4 rounded-2xl border p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">Edit Chatbot</h2>
              <p className="text-sm text-agentic-text-secondary mt-1">Update chatbot details and assigned agents.</p>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Chatbot Name</label>
                  <TextField tone="default-focus-primary"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="e.g., Pacific One Health"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Description</label>
                  <TextArea tone="default-focus-primary"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Describe this chatbot"
                    rows={4}
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div>
                  <LogoUploader
                    label="Chatbot Logo"
                    value={editLogo}
                    onChange={setEditLogo}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Assign Agents</label>
                  <div
                    className="w-full rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-agentic-primary-blue focus-within:border-transparent px-2 py-2 flex flex-wrap gap-2 relative cursor-text"
                    tabIndex={0}
                    onClick={() => setAgentPickerOpen(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAgentPickerOpen((v) => !v); } }}
                    aria-expanded={agentPickerOpen}
                    role="combobox"
                    aria-haspopup="listbox"
                  >
                    {editSelectedAgentIds.length === 0 && (
                      <span className="text-sm text-agentic-text-muted px-1 py-1">Select agents…</span>
                    )}
                    {editSelectedAgentIds.map((id) => {
                      const agent = pickerAgents.find((a) => a.id === id);
                      if (!agent) return null;
                      return (
                        <Badge tone="primary-soft" key={id} className="px-2 py-1 rounded-full text-xs inline-flex items-center gap-2">
                          {agent.name} • Last updated: {agent.lastActive}
                          <Button tone="custom"
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setEditSelectedAgentIds(editSelectedAgentIds.filter((x) => x !== id)); }}
                            className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark"
                            aria-label="Remove agent"
                          >
                            ×
                          </Button>
                        </Badge>
                      );
                    })}
                    <div className="flex-1 min-w-[200px]" />
                    <Button tone="muted-text"
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setAgentPickerOpen((v) => !v); }}
                      className="absolute right-2 top-2 px-2 py-1"
                      aria-label="Toggle agents"
                    >
                      ▾
                    </Button>

                    {agentPickerOpen && (
                      <Surface tone="card-raised" className="absolute left-0 top-full mt-2 w-full max-h-64 overflow-y-auto border rounded-xl z-10">
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                          <TextField tone="default-focus-primary"
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                            placeholder="Search agents"
                            className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                          />
                        </div>
                        <div className="p-2">
                          {agentsLoading && <div className="p-2 text-sm text-agentic-text-secondary">Loading agents...</div>}
                          {!agentsLoading && pickerAgents.length === 0 && <div className="p-2 text-sm text-agentic-text-secondary">No agents available</div>}
                          {!agentsLoading && pickerAgents.length > 0 && pickerAgents
                            .filter((a) => a.name.toLowerCase().includes(agentSearch.toLowerCase()))
                            .map((agent) => {
                              const checked = editSelectedAgentIds.includes(agent.id);
                              return (
                                <Button tone="custom"
                                  type="button"
                                  key={agent.id}
                                  onClick={() => {
                                    if (checked) setEditSelectedAgentIds(editSelectedAgentIds.filter((x) => x !== agent.id));
                                    else setEditSelectedAgentIds([...editSelectedAgentIds, agent.id]);
                                  }}
                                  className={`w-full text-left flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 ${checked ? 'bg-agentic-primary-blue/5' : ''}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Checkbox readOnly checked={checked} className="pointer-events-none" />
                                    <div>
                                      <div className="text-sm font-medium text-agentic-text-primary">{agent.name}</div>
                                      <div className="text-xs text-agentic-text-secondary">Last updated: {agent.lastActive}</div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-agentic-text-secondary">{agent.type}</span>
                                </Button>
                              );
                            })}
                        </div>
                        <div className="sticky bottom-0 bg-white p-2 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs text-agentic-text-secondary">{editSelectedAgentIds.length} selected</span>
                          <div className="flex items-center gap-2">
                            <Button tone="outline" type="button" className="px-3 py-1 text-sm rounded-lg border" onClick={() => setEditSelectedAgentIds([])}>Clear</Button>
                            <Button tone="primary" type="button" className="px-3 py-1 text-sm rounded-lg hover:opacity-90" onClick={() => setAgentPickerOpen(false)}>Done</Button>
                          </div>
                        </div>
                      </Surface>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Greeting Message</label>
                  <TextField tone="default-focus-primary"
                    value={editGreeting}
                    onChange={(e) => setEditGreeting(e.target.value)}
                    placeholder="Hello! How can I help you today?"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Placeholder Text</label>
                  <TextField tone="default-focus-primary"
                    value={editPlaceholder}
                    onChange={(e) => setEditPlaceholder(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button tone="outline-muted"
                  type="button"
                  onClick={() => closeEdit()}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </Button>
                <Button tone="primary-gradient"
                  type="button"
                  onClick={() => doUpdate()}
                  className="px-4 py-2 rounded-lg disabled:opacity-60"
                  disabled={!editName.trim() || !editDescription.trim() || editSelectedAgentIds.length === 0 || editingSaving}
                >
                  {editingSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </Surface>
        </Overlay>
      )}

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeleteId(null); }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this chatbot?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />
    </Page>
  );
};

export default Chatbots;
